import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { updateScoutCode } from "../../services/api.js";
import { trataAdvanced } from "./utils";
import { handleAdvanced } from "./utils";

const Plays = ({
  partida,
  set,
  numScout,
  onChangeScore,
  placar,
  onPlacarGeral,
  refreshKey,
  teamNames,
  ultimoSet,
  onPlay,
  playerFilter,
  onClearPlayerFilter,
  onJogadasChange,
  maxHeight,
  getVideoTime, // Adicionar prop para obter tempo do vídeo
}) => {
  const [jogadas, setJogadas] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingScoutId, setEditingScoutId] = useState(null); // ID do scout sendo editado
  const [editingCode, setEditingCode] = useState(""); // Código sendo editado
  const [editingTimeId, setEditingTimeId] = useState(null); // ID do scout sendo editado o tempo
  const [editingTime, setEditingTime] = useState(""); // Tempo sendo editado
  const timeInputRef = useRef(); // Ref separado para input de tempo
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const tableRef = useRef();
  const tableContainerRef = useRef();
  const editInputRef = useRef();

  const fetchLastMatch = async () => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/matches/set/last20`,
        {
          set: Number(set),
          partida,
          lines: 10000,
        }
      );
      setJogadas(response?.data[0]?.last20Scouts || []);

      const newPlacar = response?.data[0]?.last20Scouts.at(-1)?.score;
      if (JSON.stringify(placar) !== JSON.stringify(newPlacar)) {
        onChangeScore(newPlacar);
      }
    } catch (error) {
      console.error("Erro ao buscar as jogadas:", error);
    }
  };

  useEffect(() => {
    fetchLastMatch();
  }, [set, partida, refreshKey]);

  // Scroll automático para o final da lista
  useEffect(() => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop =
        tableContainerRef.current.scrollHeight;
    }
  }, [jogadas]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        setSelectedRows([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (parseInt(set) !== parseInt(ultimoSet)) return;
      if (
        event.ctrlKey &&
        event.key === "Backspace" &&
        selectedRows.length > 0
      ) {
        handleRemoveSelected();
        window.location.reload();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedRows, jogadas, set, ultimoSet]);

  const handleRemove = async (index, cod) => {
    const confirm = window.confirm("Deseja realmente remover esta jogada?");
    const scoutId = jogadas[index]["id"];
    if (!confirm) return;

    // const realIndex = numScout - index - 1;

    try {
      await axios.delete(
        `${
          import.meta.env.VITE_REACT_APP_BACKEND_BASEURL
        }/matches/partida/scout/remove/${partida}/${scoutId}`
      );

      // Atualiza a lista
      fetchLastMatch();
      onPlacarGeral((prev) => !prev);
      if (cod === "**nSet") {
        window.location.reload();
      }
    } catch (error) {
      console.error("Erro ao remover a jogada:", error);
      alert("Erro ao remover a jogada.");
    }
  };

  const toggleRow = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleRemoveSelected = async () => {
    for (const idx of selectedRows) {
      const scoutId = jogadas[idx]["id"];
      try {
        await axios.delete(
          `${
            import.meta.env.VITE_REACT_APP_BACKEND_BASEURL
          }/matches/partida/scout/remove/${partida}/${scoutId}`
        );
      } catch (error) {
        console.error("Erro ao remover a jogada:", error);
      }
    }
    setSelectedRows([]);
    fetchLastMatch();
    onPlacarGeral((prev) => !prev);
  };

  // Filtro de jogadas por atleta
  let jogadasFiltradas = jogadas;
  if (playerFilter && playerFilter.side && playerFilter.number) {
    const prefix =
      playerFilter.side === "home"
        ? `*${String(playerFilter.number).padStart(2, "0")}`
        : `a${String(playerFilter.number).padStart(2, "0")}`;
    jogadasFiltradas = jogadas.filter(
      (j) => typeof j.cod === "string" && j.cod.startsWith(prefix)
    );
  } else if (playerFilter && playerFilter.side && !playerFilter.number) {
    // Filtro por time inteiro
    const prefix = playerFilter.side === "home" ? "*" : "a";
    jogadasFiltradas = jogadas.filter(
      (j) => typeof j.cod === "string" && j.cod[0] === prefix
    );
  }

  // Chamar callback para informar jogadas filtradas
  useEffect(() => {
    if (onJogadasChange) onJogadasChange(jogadasFiltradas);
  }, [JSON.stringify(jogadasFiltradas)]);

  // Função utilitária para traduzir o código
  function traduzirCodigo(cod) {
    // if (!cod || cod.length < 6) return "";

    const time = cod[0];
    const codTime =
      time === "*"
        ? (teamNames?.home?.name || "").slice(0, 4)
        : (teamNames?.away?.name || "").slice(0, 4);
    const segundoCarac = cod[1];

    const num = isNaN(parseInt(segundoCarac)) ? "" : cod.slice(1, 3);
    const fundamento = cod[3];
    const qualidade = cod[5];

    const fundamentosMap = {
      S: "Saque",
      R: "Passe",
      E: "Toque",
      A: "Ataque",
      B: "Bloqueio",
      D: "Defesa",
    };

    const qualidadesMap = {
      "#": "perfeito/ponto",
      "+": "ótimo",
      "!": "bom",
      "-": "ruim",
      "/": "péssimo",
      "=": "erro",
    };

    let desc = "";

    // Casos especiais que não seguem o padrão geral
    if (fundamento === "F") {
      desc = "falta genérica";
    } else if (fundamento === "S" && qualidade === "#") {
      desc = "ace";
    } else if (segundoCarac === "$") {
      desc = "ponto manual";
    } else if (segundoCarac === "T") {
      desc = "Tempo";
    } else if (fundamento === "B" && qualidade === "#") {
      desc = "block";
    } else if (fundamento === "A" && qualidade === "<") {
      desc = "ataque bloqueado";
    } else if (fundamentosMap[fundamento] && qualidadesMap[qualidade]) {
      // Casos com qualidade '=' (erro) ou '#' (perfeito/ponto) com frases específicas
      if (qualidade === "=") {
        desc = `erro de ${fundamentosMap[fundamento].toLowerCase()}`;
      } else if (qualidade === "#") {
        if (fundamento === "A") desc = "ponto de ataque";
        else if (fundamento === "R") desc = "passe perfeito";
        else if (fundamento === "D") desc = "defesa perfeita";
        else if (fundamento === "E") desc = "toque perfeito";
        else desc = `${fundamentosMap[fundamento]} ${qualidadesMap[qualidade]}`;
      } else {
        // Casos gerais (qualidade +, !, -, /)
        desc = `${fundamentosMap[fundamento]} ${qualidadesMap[qualidade]}`;
      }
    } else {
      // Combinação não reconhecida
      return "";
    }

    return `${codTime} ${num} - ${desc}`;
  }

  // Função para iniciar edição
  const startEditing = (scoutId, currentCode) => {
    setEditingScoutId(scoutId);
    setEditingCode(currentCode.toUpperCase());
    // Foca no input após um pequeno delay para garantir que o DOM foi atualizado
    setTimeout(() => {
      if (editInputRef.current) {
        editInputRef.current.focus();
        // Posiciona o cursor no final do texto
        const length = editInputRef.current.value.length;
        editInputRef.current.setSelectionRange(length, length);
      }
    }, 10);
  };

  // Função para salvar edição
  const saveEdit = async () => {
    if (!editingScoutId || !editingCode.trim()) {
      setEditingScoutId(null);
      setEditingCode("");
      return;
    }

    try {
      const tempo = getVideoTime ? getVideoTime() : 0;

      // Primeiro aplica handleAdvanced no código
      const codigoProcessado = handleAdvanced(editingCode.trim());
      const cod = codigoProcessado.slice(0, 6);
      const codAdvanced = codigoProcessado.slice(6).toUpperCase();
      // Depois aplica trataAdvanced para extrair os campos
      const dadosAdvanced = trataAdvanced(codAdvanced);

      // Extrai os campos do objeto retornado por trataAdvanced
      const { combo, sz, ez, ez2 } = dadosAdvanced || {};

      await updateScoutCode(
        partida,
        editingScoutId,
        cod,
        tempo,
        combo,
        sz,
        ez,
        ez2
      );

      // Atualiza a lista local
      setJogadas((prevJogadas) =>
        prevJogadas.map((jogada) =>
          jogada.id === editingScoutId
            ? {
                ...jogada,
                cod,
                tempo,
                combo,
                sz,
                ez,
                ez2,
              }
            : jogada
        )
      );

      // Atualiza o placar se necessário
      onPlacarGeral((prev) => !prev);

      setEditingScoutId(null);
      setEditingCode("");
    } catch (error) {
      console.error("Erro ao atualizar código:", error);
      alert("Erro ao atualizar o código. Tente novamente.");
    }
  };

  // Função para cancelar edição
  const cancelEdit = () => {
    setEditingScoutId(null);
    setEditingCode("");
  };

  // Handler para teclas no input de edição
  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEdit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  // Função para iniciar edição do tempo
  const startEditingTime = (scoutId, currentTime) => {
    setEditingTimeId(scoutId);
    setEditingTime(String(Math.round(currentTime || 0)));
    setTimeout(() => {
      if (timeInputRef.current) {
        timeInputRef.current.focus();
        const length = timeInputRef.current.value.length;
        timeInputRef.current.setSelectionRange(length, length);
      }
    }, 10);
  };

  // Função para salvar edição do tempo
  const saveEditTime = async () => {
    if (!editingTimeId || editingTime.trim() === "") {
      setEditingTimeId(null);
      setEditingTime("");
      return;
    }
    try {
      const scout = jogadas.find((j) => j.id === editingTimeId);
      if (!scout) return;
      const tempo = parseInt(editingTime, 10);
      await updateScoutCode(
        partida,
        editingTimeId,
        scout.cod,
        tempo,
        scout.combo,
        scout.sz,
        scout.ez,
        scout.ez2
      );
      setJogadas((prevJogadas) =>
        prevJogadas.map((jogada) =>
          jogada.id === editingTimeId ? { ...jogada, tempo } : jogada
        )
      );
      setEditingTimeId(null);
      setEditingTime("");
    } catch (error) {
      alert("Erro ao atualizar o tempo. Tente novamente.");
      setEditingTimeId(null);
      setEditingTime("");
    }
  };

  // Função para cancelar edição do tempo
  const cancelEditTime = () => {
    setEditingTimeId(null);
    setEditingTime("");
  };

  // Handler para teclas no input de edição de tempo
  const handleEditTimeKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveEditTime();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditTime();
    }
  };

  return (
    <div className="text-white mb-6 max-w-full">
      {playerFilter && onClearPlayerFilter && (
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-blue-700 font-semibold">
            Filtrando atleta:{" "}
            {playerFilter.side === "home"
              ? teamNames?.home?.name
              : teamNames?.away?.name}{" "}
            #{playerFilter.number}
          </span>
          <button
            className="ml-2 text-xs text-gray-400 hover:text-red-500"
            onClick={onClearPlayerFilter}
            title="Limpar filtro"
          >
            ✕
          </button>
        </div>
      )}
      {jogadasFiltradas.length === 0 && <p>Carregando jogadas...</p>}
      {/* 
      {!isAdminRoute && (
        <span className="text-gray-400 text-center block mb-1">
          <p>Evolução dos pontos</p>
        </span>
      )} */}
      <div
        ref={tableContainerRef}
        className={
          maxHeight ? "w-full" : "h-full max-h-full min-h-0 overflow-y-auto"
        }
        style={
          maxHeight
            ? { maxHeight: maxHeight, overflowY: "auto" }
            : {
                height: "100%",
                maxHeight: "100%",
                minHeight: 0,
                overflowY: "auto",
              }
        }
      >
        <table ref={tableRef} className="w-4/5 table-fixed text-[10px]">
          <thead
            className="bg-gray-100"
            style={{ position: "sticky", top: 0, zIndex: 1 }}
          >
            <tr className="text-gray-400">
              {/* {isAdminRoute && <th className="text-left w-1/3">Código</th>} */}
              <th
                className="text-left py-0 text-[10px]"
                style={{ width: 50, minWidth: 50, maxWidth: 50 }}
              >
                Código
              </th>
              {/* NEW ZONE COLUMN HEADER */}
              <th
                className="text-center py-0 text-[8px]"
                style={{ width: 40, minWidth: 40, maxWidth: 40 }}
              >
                Zona
              </th>
              <th
                className="text-left py-0 text-[10px]"
                style={{ width: 30, minWidth: 30, maxWidth: 30 }}
              >
                {(teamNames?.home?.name || "").slice(0, 4)}
              </th>
              <th
                className="text-left py-0 text-[10px]"
                style={{ width: 30, minWidth: 30, maxWidth: 30 }}
              >
                {(teamNames?.away?.name || "").slice(0, 4)}
              </th>
              <th
                className="text-left py-0 text-[10px]"
                style={{ width: 30, minWidth: 30, maxWidth: 30 }}
              >
                V
              </th>
              <th
                className="text-left py-0 text-[10px]"
                style={{ width: 150, minWidth: 150, maxWidth: 150 }}
              >
                Descrição
              </th>
              <th
                className="text-center py-0 text-[9px]"
                style={{ width: 32, minWidth: 32, maxWidth: 32 }}
              >
                Tempo
              </th>
            </tr>
          </thead>
          <tbody className="text-[10px]">
            {jogadasFiltradas.map((j, idx, arr) => {
              const prev = arr[idx - 1];
              const showScore0 = !prev || j.score?.[0] !== prev.score?.[0];
              const showScore1 = !prev || j.score?.[1] !== prev.score?.[1];
              const traducao = traduzirCodigo(j.cod);
              const time = j.cod?.[0];
              const resultado = j.cod?.[5];
              const fundamento = j.cod?.[3];

              return (
                <tr
                  key={idx}
                  className={selectedRows.includes(idx) ? "bg-gray-200" : ""}
                  onClick={() => toggleRow(idx)}
                  style={{ cursor: "pointer" }}
                >
                  {/* {isAdminRoute && <td className="text-gray-400">{j.cod}</td>} */}
                  <td
                    className="text-gray-400 py-0 text-[10px]"
                    style={{
                      width: 30,
                      minWidth: 30,
                      maxWidth: 30,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {editingScoutId === j.id ? (
                      <input
                        type="text"
                        ref={editInputRef}
                        value={editingCode}
                        onChange={(e) => setEditingCode(e.target.value)}
                        onKeyDown={handleEditKeyDown}
                        onBlur={cancelEdit}
                        className="w-full bg-transparent text-gray-400 text-[10px] outline-none"
                      />
                    ) : (
                      <span
                        className={
                          "text-[10px] " +
                          ((time === "*" &&
                            resultado === "#" &&
                            !["D", "E", "R"].includes(fundamento)) ||
                          (time === "a" &&
                            resultado === "=" &&
                            !["B", "R"].includes(fundamento))
                            ? "text-blue-600"
                            : (time === "a" &&
                                resultado === "#" &&
                                !["D", "E", "R"].includes(fundamento)) ||
                              (time === "*" &&
                                resultado === "=" &&
                                !["B", "R"].includes(fundamento))
                            ? "text-red-600"
                            : "text-gray-400")
                        }
                        onClick={() =>
                          isAdminRoute ? startEditing(j.id, j.cod) : null
                        }
                        style={{
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          display: "block", // Span precisa de display block/inline-block para overflow
                        }}
                      >
                        {j.cod}
                      </span>
                    )}
                  </td>
                  {/* NEW ZONE COLUMN CELL */}
                  <td
                    className="text-center py-0 text-[8px] text-blue-400"
                    style={{ width: 40, minWidth: 40, maxWidth: 40 }}
                  >
                    {j.sz && j.ez && (
                      <span>
                        {j.sz}&#8594;{j.ez}
                        {j?.ez2}
                      </span>
                    )}
                  </td>
                  <td
                    className="text-left py-0 text-[10px]"
                    style={{ width: 30, minWidth: 30, maxWidth: 30 }}
                  >
                    {showScore0 && (
                      <span className="bg-blue-500 text-white px-1 py-0.5 rounded text-[10px]">
                        {j.score?.[0]}
                      </span>
                    )}
                  </td>
                  <td
                    className="text-left py-0 text-[10px]"
                    style={{ width: 30, minWidth: 30, maxWidth: 30 }}
                  >
                    {showScore1 && (
                      <span className="bg-red-500 text-white px-1 py-0.5 rounded text-[10px]">
                        {j.score?.[1]}
                      </span>
                    )}
                  </td>
                  {/* Botão play */}
                  <td
                    className="text-left py-0 text-[10px]"
                    style={{ width: 30, minWidth: 30, maxWidth: 30 }}
                  >
                    {j.tempo && (
                      <button
                        onClick={() => onPlay(j.tempo)}
                        title="Ir para o tempo da jogada"
                        className="text-[10px]"
                      >
                        ▶️
                      </button>
                    )}
                  </td>
                  {/* Nova coluna de tradução */}
                  <td
                    className="text-left py-0 text-[10px]"
                    style={{ width: 150, minWidth: 150, maxWidth: 150 }}
                  >
                    {traducao && (
                      <span
                        className={
                          "text-[10px] " +
                          ((time === "*" &&
                            resultado === "#" &&
                            !["D", "E", "R"].includes(fundamento)) ||
                          (time === "a" &&
                            resultado === "=" &&
                            !["B", "R"].includes(fundamento))
                            ? "text-blue-600"
                            : (time === "a" &&
                                resultado === "#" &&
                                !["D", "E", "R"].includes(fundamento)) ||
                              (time === "*" &&
                                resultado === "=" &&
                                !["B", "R"].includes(fundamento))
                            ? "text-red-600"
                            : "text-gray-400")
                        }
                      >
                        {traducao}
                      </span>
                    )}
                  </td>
                  {/* Nova coluna Tempo */}
                  <td
                    className="text-center py-0 text-[9px] text-blue-400"
                    style={{ width: 32, minWidth: 32, maxWidth: 32 }}
                    onClick={
                      isAdminRoute
                        ? (e) => {
                            e.stopPropagation();
                            startEditingTime(j.id, j.tempo);
                          }
                        : undefined
                    }
                  >
                    {isAdminRoute && editingTimeId === j.id ? (
                      <input
                        type="number"
                        ref={timeInputRef}
                        value={editingTime}
                        onChange={(e) =>
                          setEditingTime(e.target.value.replace(/\D/g, ""))
                        }
                        onKeyDown={handleEditTimeKeyDown}
                        onBlur={cancelEditTime}
                        className="w-full bg-transparent text-blue-400 text-[9px] outline-none text-center"
                        min={0}
                        style={{ maxWidth: 30 }}
                      />
                    ) : (
                      <span
                        className="text-[9px] text-blue-700 cursor-pointer"
                        title={isAdminRoute ? "Editar tempo" : undefined}
                      >
                        {j.tempo !== undefined && j.tempo !== null
                          ? Math.round(j.tempo)
                          : "-"}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Plays;
